import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FADE_MS = 1500;

const useReducedMotion = () => {
    const [reduced, setReduced] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    useEffect(() => {
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        const fn = (e) => setReduced(e.matches);
        mq.addEventListener("change", fn);
        return () => mq.removeEventListener("change", fn);
    }, []);
    return reduced;
};

const preload = (slides) =>
    Promise.all(
        slides.flatMap((s) => [s.url, s.url_mobile]).map(
            (src) =>
                new Promise((res) => {
                    const img = new Image();
                    img.onload = img.onerror = res;
                    img.src = src;
                })
        )
    );

export default function HeroCarousel({ paused = false }) {
    const [data, setData] = useState(null);
    const [ready, setReady] = useState(false);
    const [active, setActive] = useState(0);
    const reduced = useReducedMotion();
    const timer = useRef(null);

    useEffect(() => {
        axios.get(`${API}/hero`).then(async (r) => {
            setData(r.data);
            await preload(r.data.slides);
            setReady(true);
        }).catch(() => {});
    }, []);

    const slides = data?.slides || [];
    const intervalMs = (data?.interval_seconds || 30) * 1000;

    const restart = useCallback(() => {
        clearInterval(timer.current);
        if (slides.length < 2 || paused) return;
        timer.current = setInterval(() => setActive((a) => (a + 1) % slides.length), intervalMs);
    }, [slides.length, intervalMs, paused]);

    useEffect(() => {
        restart();
        return () => clearInterval(timer.current);
    }, [restart]);

    const goTo = (i) => {
        setActive(i);
        restart();
    };

    if (!slides.length) return null;

    return (
        <>
            <div className="absolute inset-0 overflow-hidden" aria-hidden="true" data-testid="hero-carousel">
                {slides.map((s, i) => {
                    const isActive = i === active;
                    return (
                        <picture
                            key={s.id}
                            className="absolute inset-0"
                            style={{ opacity: ready && isActive ? 1 : 0, transition: `opacity ${FADE_MS}ms ease-in-out` }}
                            data-testid={`hero-slide-${i}`}
                            data-active={isActive}
                        >
                            <source media="(max-width: 767px)" srcSet={s.url_mobile} />
                            <img
                                src={s.url}
                                alt=""
                                className={`h-full w-full object-cover ${isActive && !reduced ? "hero-kenburns" : ""}`}
                                style={isActive && !reduced ? { animationDuration: `${intervalMs}ms` } : undefined}
                                loading="eager"
                                decoding="async"
                            />
                        </picture>
                    );
                })}
                <div className="absolute inset-0 bg-gradient-to-b from-navy/55 via-navy/60 to-navy" />
            </div>

            {slides.length > 1 && (
                <div className="absolute bottom-6 right-6 z-20 flex items-center gap-2.5 md:bottom-8 md:right-8" role="tablist" aria-label="Imágenes del inicio" data-testid="hero-dots">
                    {slides.map((s, i) => (
                        <button
                            key={s.id}
                            type="button"
                            role="tab"
                            aria-selected={i === active}
                            aria-label={`Imagen ${i + 1}: ${s.alt}`}
                            onClick={() => goTo(i)}
                            className={`h-2 rounded-full transition-all duration-500 ${i === active ? "w-6 bg-gold" : "w-2 bg-gold/40 hover:bg-gold/70"}`}
                            data-testid={`hero-dot-${i}`}
                        />
                    ))}
                </div>
            )}
        </>
    );
}
